/**
 * ConditionRecordDialog — extracted from ConditionRecords.js.
 * Receives all state + helpers from parent via `ctx` bag.
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Loader2, Save, Layers, Plus, Trash2 } from 'lucide-react';

const _uid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(36).slice(2)}`;

const ConditionRecordDialog = ({ ctx }) => {
  const {
    isDialogOpen,
    setIsDialogOpen,
    editingRecord,
    formData,
    setFormData,
    isSaving,
    conditionTypes,
    conditionTables,
    accessSequences,
    handleSubmit,
    getFilteredConditionTables,
    getKeyFieldsForTable,
    getCalculationTypeInfo,
    renderKeyField,
  } = ctx;

  return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingRecord ? 'Edit Condition Record' : 'Create Condition Record'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Condition Type & Table */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Condition Type *</Label>
                  <Select 
                    value={formData.condition_type_id || '__SELECT__'} 
                    onValueChange={(v) => {
                      const newTypeId = v === '__SELECT__' ? '' : v;
                      // Reset condition table when condition type changes
                      setFormData({...formData, condition_type_id: newTypeId, condition_table_id: ''});
                    }}
                    disabled={!!editingRecord}
                  >
                    <SelectTrigger><SelectValue placeholder="Select Condition Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__SELECT__">Select Condition Type</SelectItem>
                      {conditionTypes.map(ct => (
                        <SelectItem key={ct.condition_type_id} value={ct.condition_type_id}>
                          {ct.condition_type_id} - {ct.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Condition Table *</Label>
                  <Select 
                    value={formData.condition_table_id || '__SELECT__'} 
                    onValueChange={(v) => setFormData({...formData, condition_table_id: v === '__SELECT__' ? '' : v})}
                    disabled={!!editingRecord || !formData.condition_type_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={formData.condition_type_id ? "Select Condition Table" : "Select Condition Type first"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__SELECT__">Select Condition Table</SelectItem>
                      {getFilteredConditionTables().map(table => (
                        <SelectItem key={table.condition_table_id} value={table.condition_table_id}>
                          {table.condition_table_id} - {table.description}
                        </SelectItem>
                      ))}
                      {getFilteredConditionTables().length === 0 && formData.condition_type_id && (
                        <SelectItem value="__NONE__" disabled>No tables in Access Sequence</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {formData.condition_type_id && (
                    <p className="text-xs text-gray-500 mt-1">
                      Tables filtered by Access Sequence: {conditionTypes.find(ct => ct.condition_type_id === formData.condition_type_id)?.access_sequence_id || 'None'}
                    </p>
                  )}
                </div>
              </div>

              {/* Key Fields - Dynamic based on Condition Table */}
              {formData.condition_table_id && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    Key Fields for Table: {formData.condition_table_id}
                  </h3>
                  
                  {/* Header Fields */}
                  {getKeyFieldsForTable().headerFields.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                        <span className="text-xs font-medium text-orange-700">Header Level Fields (Document level - single value)</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pl-5 border-l-2 border-orange-200">
                        {getKeyFieldsForTable().headerFields.map(field => renderKeyField(field, true))}
                      </div>
                    </div>
                  )}
                  
                  {/* Item Fields */}
                  {getKeyFieldsForTable().itemFields.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span className="text-xs font-medium text-green-700">Item Level Fields (Line item level - can vary per line)</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pl-5 border-l-2 border-green-200">
                        {getKeyFieldsForTable().itemFields.map(field => renderKeyField(field, false))}
                      </div>
                    </div>
                  )}
                  
                  {getKeyFieldsForTable().headerFields.length === 0 && getKeyFieldsForTable().itemFields.length === 0 && (
                    <p className="text-sm text-gray-500 italic">No key fields defined for this table</p>
                  )}
                </div>
              )}
              
              {!formData.condition_table_id && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Key Fields</h3>
                  <p className="text-sm text-gray-500 italic">Select a Condition Table to see the key fields</p>
                </div>
              )}

              {/* Condition Values */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Condition Values</h3>
                  {getCalculationTypeInfo() && (
                    <span className="text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded">
                      Calculation Type: {getCalculationTypeInfo().label}
                    </span>
                  )}
                </div>
                
                {!formData.condition_type_id ? (
                  <p className="text-sm text-gray-500 italic">Select a Condition Type to configure values</p>
                ) : (
                  <div className="grid grid-cols-5 gap-4">
                    {/* Amount */}
                    <div>
                      <Label>{getCalculationTypeInfo()?.amountLabel || 'Amount/Rate'} *</Label>
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.condition_amount}
                          onChange={(e) => setFormData({...formData, condition_amount: e.target.value})}
                          placeholder={getCalculationTypeInfo()?.amountPlaceholder || 'e.g., 100.00'}
                          required
                          className={getCalculationTypeInfo()?.amountSuffix ? 'pr-8' : ''}
                        />
                        {getCalculationTypeInfo()?.amountSuffix && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                            {getCalculationTypeInfo().amountSuffix}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Currency */}
                    <div>
                      <Label>Currency</Label>
                      {getCalculationTypeInfo()?.currencyDisabled ? (
                        <Input
                          value={getCalculationTypeInfo()?.label === 'Percentage' ? 'N/A' : 'Company Currency'}
                          disabled
                          className="bg-gray-100 text-gray-500"
                        />
                      ) : (
                        <Select value={formData.condition_currency} onValueChange={(v) => setFormData({...formData, condition_currency: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="INR">INR</SelectItem>
                            <SelectItem value="GBP">GBP</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    
                    {/* Per Unit / Pricing Unit */}
                    <div>
                      <Label>Pricing Unit</Label>
                      {getCalculationTypeInfo()?.perUnitDisabled ? (
                        <Input
                          value="N/A"
                          disabled
                          className="bg-gray-100 text-gray-500"
                        />
                      ) : (
                        <Input
                          type="number"
                          step="1"
                          min="1"
                          value={formData.per_unit}
                          onChange={(e) => setFormData({...formData, per_unit: e.target.value})}
                          placeholder={String(getCalculationTypeInfo()?.defaultPerUnit || 1)}
                        />
                      )}
                    </div>
                    
                    {/* UoM */}
                    <div>
                      <Label>UoM</Label>
                      {getCalculationTypeInfo()?.uomDisabled ? (
                        <Input
                          value="N/A"
                          disabled
                          className="bg-gray-100 text-gray-500"
                        />
                      ) : (
                        <Select 
                          value={formData.unit_of_measure || getCalculationTypeInfo()?.defaultUoM || 'EA'} 
                          onValueChange={(v) => setFormData({...formData, unit_of_measure: v})}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {/* Filter UoM options based on calculation type */}
                            {(() => {
                              const calcType = conditionTypes.find(ct => ct.condition_type_id === formData.condition_type_id)?.calculation_type;
                              
                              // Quantity type (C) - only quantity units
                              if (calcType === 'C') {
                                return (
                                  <>
                                    <SelectItem value="EA">EA (Each)</SelectItem>
                                    <SelectItem value="PC">PC (Piece)</SelectItem>
                                  </>
                                );
                              }
                              
                              // Net Weight (E) or Gross Weight (D) - only weight units
                              if (calcType === 'D' || calcType === 'E') {
                                return (
                                  <>
                                    <SelectItem value="KG">KG (Kilogram)</SelectItem>
                                    <SelectItem value="G">G (Gram)</SelectItem>
                                  </>
                                );
                              }
                              
                              // Volume (F) - only volume units
                              if (calcType === 'F') {
                                return (
                                  <>
                                    <SelectItem value="M3">M³ (Cubic Meter)</SelectItem>
                                    <SelectItem value="L">L (Liter)</SelectItem>
                                  </>
                                );
                              }
                              
                              // Default - show all (shouldn't reach here normally)
                              return (
                                <>
                                  <SelectItem value="EA">EA (Each)</SelectItem>
                                  <SelectItem value="PC">PC (Piece)</SelectItem>
                                  <SelectItem value="KG">KG (Kilogram)</SelectItem>
                                  <SelectItem value="G">G (Gram)</SelectItem>
                                  <SelectItem value="L">L (Liter)</SelectItem>
                                  <SelectItem value="M3">M³ (Cubic Meter)</SelectItem>
                                  <SelectItem value="M">M (Meter)</SelectItem>
                                </>
                              );
                            })()}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    
                    {/* Calculation Type Display */}
                    <div>
                      <Label>Calc. Type</Label>
                      <Input
                        value={getCalculationTypeInfo()?.label || 'Select Condition Type'}
                        disabled
                        className="bg-gray-100 text-gray-600 font-medium"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Scale Pricing */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-orange-600" />
                    <h3 className="text-sm font-semibold text-gray-700">Scale Pricing</h3>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.has_scales}
                      onChange={(e) => setFormData({...formData, has_scales: e.target.checked, scale_lines: e.target.checked ? formData.scale_lines : []})}
                      className="rounded border-gray-300"
                    />
                    Enable Scale Pricing
                  </label>
                </div>
                
                {formData.has_scales && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Scale Basis</Label>
                        <Select value={formData.scale_basis || 'B'} onValueChange={(v) => setFormData({...formData, scale_basis: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="B">Quantity</SelectItem>
                            <SelectItem value="C">Value/Amount</SelectItem>
                            <SelectItem value="D">Gross Weight</SelectItem>
                            <SelectItem value="E">Net Weight</SelectItem>
                            <SelectItem value="F">Volume</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-end">
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => setFormData({
                            ...formData, 
                            scale_lines: [...formData.scale_lines, { _uid: _uid(), scale_quantity: '', condition_amount: '' }]
                          })}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add Scale Line
                        </Button>
                      </div>
                    </div>

                    {formData.scale_lines.length > 0 && (
                      <div className="bg-white rounded border p-3">
                        <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 mb-2">
                          <div className="col-span-5">From {formData.scale_basis === 'B' ? 'Qty' : formData.scale_basis === 'C' ? 'Value' : 'Weight/Vol'}</div>
                          <div className="col-span-5">Amount/Rate</div>
                          <div className="col-span-2"></div>
                        </div>
                        {formData.scale_lines.map((line, index) => (
                          <div key={line._uid || `legacy_${index}`} className="grid grid-cols-12 gap-2 mb-2 items-center">
                            <div className="col-span-5">
                              <Input
                                type="number"
                                step="1"
                                min="0"
                                value={line.scale_quantity}
                                onChange={(e) => {
                                  const newLines = [...formData.scale_lines];
                                  newLines[index].scale_quantity = e.target.value;
                                  setFormData({...formData, scale_lines: newLines});
                                }}
                                placeholder={index === 0 ? "0" : "e.g., 100"}
                                className="text-sm"
                              />
                            </div>
                            <div className="col-span-5">
                              <Input
                                type="number"
                                step="0.01"
                                value={line.condition_amount}
                                onChange={(e) => {
                                  const newLines = [...formData.scale_lines];
                                  newLines[index].condition_amount = e.target.value;
                                  setFormData({...formData, scale_lines: newLines});
                                }}
                                placeholder="e.g., 95.00"
                                className="text-sm"
                              />
                            </div>
                            <div className="col-span-2 flex justify-end">
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="sm"
                                className="text-red-500 h-8 w-8 p-0"
                                onClick={() => {
                                  const newLines = formData.scale_lines.filter((_, i) => i !== index);
                                  setFormData({...formData, scale_lines: newLines});
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        <p className="text-xs text-gray-500 mt-2">
                          Example: Qty 0 = $100, Qty 100 = $95, Qty 500 = $90 (price decreases as quantity increases)
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Validity */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valid From *</Label>
                  <Input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Valid To *</Label>
                  <Input
                    type="date"
                    value={formData.valid_to}
                    onChange={(e) => setFormData({...formData, valid_to: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="is_active">Active</Label>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t mt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving} data-testid="submit-condition-record">
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {editingRecord ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingRecord ? 'Update Condition Record' : 'Create Condition Record'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
  );
};

export default ConditionRecordDialog;
